import React, { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Gamepad2 } from 'lucide-react'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import GameControlPanel from './GameControlPanel'
import { useAppSelector } from '@/redux/hooks'
import { getAccessToken } from '@/utils/authUtils'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import {
  connectTimerWebSocket,
  disconnectTimerWebSocket,
} from '@/redux/asyncThunks/timerWebsocket'
import { fetchTimerStatusThunk } from '@/redux/asyncThunks/timerConfiguration'
import { useGamePhase } from '@/hooks/useGamePhase'
import { cn } from '@/lib/utils'

interface FloatingRoomControlsProps {
  roomId?: string
}

const FloatingRoomControls: React.FC<FloatingRoomControlsProps> = ({
  roomId,
}) => {
  const [isFloatingControlsOpen, setIsFloatingControlsOpen] = useState(false)
  const floatingControlsRef = useRef<HTMLDivElement>(null)
  const params = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const { isGameStarted, isGameCompleted } = useGamePhase()
  const { isTestChatOpen } = useAppSelector((state) => state.testChatMessages)
  const aiPlayerChatBoxesOpen = useAppSelector(
    (state) => state.aiPlayerTestChatMessages.openChatBoxes.length > 0
  )

  const currentRoomId = roomId || params.roomId

  const timerWebsocketConnected = useAppSelector(
    (state) => state.timerWS.isConnected
  )
  const jwtToken = getAccessToken()

  useEffect(() => {
    if (jwtToken && roomId && !timerWebsocketConnected) {
      dispatch(connectTimerWebSocket({ jwtToken, roomId }))
    }
  }, [dispatch, jwtToken, roomId, timerWebsocketConnected])

  useEffect(() => {
    if (roomId && timerWebsocketConnected) {
      dispatch(fetchTimerStatusThunk(roomId))
    }
  }, [dispatch, roomId, timerWebsocketConnected])

  useEffect(() => {
    return () => {
      dispatch(disconnectTimerWebSocket())
    }
  }, [dispatch])

  if (!currentRoomId) {
    return null
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        floatingControlsRef.current &&
        !floatingControlsRef.current.contains(event.target as Node)
      ) {
        setIsFloatingControlsOpen(false)
      }
    }

    if (isFloatingControlsOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isFloatingControlsOpen])

  return (
    <div
      ref={floatingControlsRef}
      className={cn(
        'fixed z-50 select-none transition-[left] ease-in-out duration-300 [top:calc(100vh-74px)]',
        isTestChatOpen || aiPlayerChatBoxesOpen ? 'left-4' : '[left:calc(100vw-74px)]'
      )}
    >
      <DropdownMenu
        open={isFloatingControlsOpen}
        onOpenChange={setIsFloatingControlsOpen}
      >
        <DropdownMenuTrigger asChild>
          <Button
            className='bg-dark-blue hover:bg-dark-blue-hovered rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center w-[50px] h-[50px]'
            aria-label='Room controls'
            disabled={!isGameStarted || isGameCompleted}
          >
            <Gamepad2
              className={cn(
                'min-w-8 min-h-8 transition-transform duration-200',
                isFloatingControlsOpen ? 'rotate-12' : 'rotate-0'
              )}
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align='end' className='w-[500px] mb-2 p-0'>
          <GameControlPanel />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default FloatingRoomControls
